
import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  computed,
  onMounted,
} from 'vue';

import PrimeVueSidebar from 'primevue/sidebar';

import PrimeVuePaginator from 'primevue/paginator';

import PrimeVueScrollTop from 'primevue/scrolltop';

import type {
  EntriesPaginator,
  EntriesPaginatorEvent,
  Projects,
  Countries,
  FeedsCreatives,
} from '@/types';

import {
  ENTRIES,
  ENTRIES_PER_PAGE,
  ENTRIES_SORT_BY_ID,
  ENTRIES_SORT_BY_DIRECTION_ASC,
  ENTRIES_SORT_BY_DIRECTION_DESC,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

export default defineComponent({
  components: {
    PrimeVueSidebar,
    PrimeVuePaginator,
    PrimeVueScrollTop,
    FeedCreativeCard: defineAsyncComponent(() => import('@/components/FeedCreativeCard.vue')),
  },
  setup() {
    const { activeUser: user } = useUser();

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects', ENTRIES);

    const {
      entries: countries,
      entriesLoading: countriesLoading,
      fetchEntries: fetchCountries,
    } = useEntriesNext<Countries>('/api/country/getCountries', ENTRIES);

    const feedsCreatives = ref<FeedsCreatives>({ ...ENTRIES });

    const feedsCreativesLoading = ref(false);

    const feedsCreativesFilters = useUrlState('filters', {
      project_id: null,
      feed_id: null,
      country_id: null,
      keyword: null,
      min_bid: null,
    });

    const feedsCreativesFiltersShow = ref(false);

    const feedsParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? feedsCreativesFilters.project_id : undefined,
    }));

    const paginator = ref<EntriesPaginator>({
      entries_per_page: ENTRIES_PER_PAGE,
      page: 1,
      sort_by: ENTRIES_SORT_BY_ID,
      sort_by_direction: ENTRIES_SORT_BY_DIRECTION_DESC,
    });

    const fetchFeedsCreatives = async (): Promise<void> => {
      feedsCreativesLoading.value = true;

      const response = await axios.post(
        'api/feed-creative/getFeedsCreativesPaginator',
        {
          ...feedsCreativesFilters,
          ...paginator.value,
        },
      );

      feedsCreatives.value = response.data;

      feedsCreativesLoading.value = false;
    };

    const onPaginatorChange = async (event: EntriesPaginatorEvent): Promise<void> => {
      paginator.value.page = event.first / ENTRIES_PER_PAGE + 1;

      if (event.sortField) {
        paginator.value.sort_by = event.sortField;

        paginator.value.sort_by_direction = event.sortOrder === -1 ? ENTRIES_SORT_BY_DIRECTION_DESC : ENTRIES_SORT_BY_DIRECTION_ASC;
      }

      await fetchFeedsCreatives();
    };

    const paginatorEvents = {
      page: onPaginatorChange,
    };

    onMounted((): void => {
      Promise.all([
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
        fetchCountries(),
        fetchFeedsCreatives(),
      ]);
    });

    return {
      ENTRIES_PER_PAGE,
      user,
      projects,
      projectsLoading,
      countries,
      countriesLoading,
      feedsCreatives,
      feedsCreativesLoading,
      feedsCreativesFilters,
      feedsCreativesFiltersShow,
      feedsParams,
      fetchFeedsCreatives,
      paginatorEvents,
    };
  },
});
